import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, PartyPopper } from "lucide-react";
import { Button } from "./ui/button";

const STORAGE_KEY = "wcei_tutorial_seen";

const STEPS = [
  {
    selectors: ["[data-testid='header-app-icon']"],
    title: "Bienvenue sur World Class EI",
    description: "Votre application d'audit croisé Isolation Énergétique. Ce guide rapide vous montre l'essentiel en quelques secondes.",
  },
  {
    selectors: ["[data-testid='desktop-nav-home']", "[data-testid='nav-home-tab']"],
    title: "Tableau de bord",
    description: "Retrouvez ici vos audits en cours, vos actions à traiter et vos indicateurs personnels.",
  },
  {
    selectors: ["[data-testid='desktop-nav-audits']", "[data-testid='nav-audits-tab']"],
    title: "Audits Terrain",
    description: "Lancez un audit question par question, avec dictée vocale et prise de photo directement depuis le terrain.",
  },
  {
    selectors: ["[data-testid='desktop-nav-actions']", "[data-testid='nav-actions-tab']"],
    title: "Plan d'Actions",
    description: "Suivez et clôturez les actions correctives issues des audits, avec échéances et responsables.",
  },
  {
    selectors: ["[data-testid='desktop-nav-dashboard']", "[data-testid='nav-analytics-tab']"],
    title: "Analyses & Heatmap",
    description: "Consultez le dashboard national, la heatmap multi-sites et la comparaison 2024 vs 2026.",
  },
  {
    selectors: ["[data-testid='help-nav-button']"],
    title: "Besoin d'aide ?",
    description: "Ce guide reste disponible à tout moment ici, avec la FAQ complète et la politique de confidentialité.",
  },
];

export function resetTutorial() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event("wcei-tutorial-restart"));
}

export default function OnboardingTour() {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(-1);
  const [rect, setRect] = useState(null);

  const findRect = useCallback((selectors) => {
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) return el.getBoundingClientRect();
    }
    return null;
  }, []);

  const start = useCallback(() => setStepIndex(0), []);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      const t = setTimeout(start, 700);
      return () => clearTimeout(t);
    }
  }, [start]);

  useEffect(() => {
    window.addEventListener("wcei-tutorial-restart", start);
    return () => window.removeEventListener("wcei-tutorial-restart", start);
  }, [start]);

  useEffect(() => {
    if (stepIndex < 0) return;
    const update = () => setRect(findRect(STEPS[stepIndex].selectors));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [stepIndex, findRect]);

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setStepIndex(-1);
  };

  const next = () => {
    if (stepIndex >= STEPS.length - 1) {
      finish();
      navigate("/aide");
      return;
    }
    setStepIndex((i) => i + 1);
  };

  if (stepIndex < 0) return null;
  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  const tooltipStyle = rect
    ? {
        top: Math.min(rect.bottom + 12, window.innerHeight - 220),
        left: Math.max(12, Math.min(rect.left, window.innerWidth - 320)),
      }
    : { top: "40%", left: "50%", transform: "translate(-50%,-50%)" };

  return (
    <div className="fixed inset-0 z-[9998]" data-testid="onboarding-tour-overlay">
      <div
        className="fixed inset-0 transition-all duration-300"
        style={
          rect
            ? {
                boxShadow: "0 0 0 9999px rgba(2,6,23,0.78)",
                position: "fixed",
                top: rect.top - 6,
                left: rect.left - 6,
                width: rect.width + 12,
                height: rect.height + 12,
                borderRadius: 10,
                border: "2px solid #10B981",
              }
            : { background: "rgba(2,6,23,0.78)" }
        }
      />
      <div
        className="fixed w-[300px] bg-slate-900 text-white rounded-xl shadow-2xl border border-emerald-500/40 p-4 space-y-2"
        style={tooltipStyle}
        data-testid="onboarding-tour-tooltip"
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-emerald-400">{step.title}</h3>
          <button data-testid="onboarding-tour-close" onClick={finish} className="text-slate-400 hover:text-white shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-slate-300 leading-relaxed">{step.description}</p>
        <div className="flex items-center justify-between pt-1">
          <span className="text-[11px] text-slate-500 font-mono">{stepIndex + 1} / {STEPS.length}</span>
          <Button size="sm" data-testid="onboarding-tour-next" onClick={next} className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs gap-1">
            {isLast ? (<>Terminer <PartyPopper className="h-3.5 w-3.5" /></>) : (<>Suivant <ArrowRight className="h-3.5 w-3.5" /></>)}
          </Button>
        </div>
      </div>
    </div>
  );
}
